import { AlertConfig } from '../user.service';
import { DEFAULT_NOTIFY_WINDOW } from '../../configuration/configuration.defaults';
import { OrgActiveAlert, OrgAlertHistoryItem } from '../patient.model';
import {
  buildAlertCycle,
  resolveAlertTimeZone,
  slotToEpoch,
} from '../../notification/model/alert-cycle.util';
import {
  cycleStatusBadgeVariant,
  CycleStatusKey,
  CYCLE_STATUS_LABELS,
} from '../../notification/cycle-status.util';
import {
  formatAlertStartedTime,
  formatNextAlertAttemptLabel,
} from '../../notification/utils/alert-progress.util';

export type ActiveCampaignGroup = {
  campaignId: string;
  title: string;
  rows: OrgActiveAlert[];
};

export type CampaignTimelineDot = {
  index: number;
  epochMs: number;
  percent: number;
  done: boolean;
  isNext: boolean;
};

export type NowLabelAlign = 'left' | 'center' | 'right';

export type CampaignTimelineModel = {
  startMs: number;
  endMs: number;
  nowMs: number;
  nowPercent: number;
  showNowMarker: boolean;
  nowLabelAlign: NowLabelAlign;
  dots: CampaignTimelineDot[];
  startLabel: string;
  endLabel: string;
  nowLabel: string;
};

export type StatusCountEntry = {
  key: CycleStatusKey;
  label: string;
  count: number;
};

export type StatusCountWithPercent = StatusCountEntry & { percent: number };

const STATUS_BAR_ORDER: CycleStatusKey[] = [
  'inProgress',
  'acknowledged',
  'connected',
  'canceled',
  'noResponse',
  'unknown',
];

export function groupActiveAlertsByCampaign(alerts: OrgActiveAlert[]): ActiveCampaignGroup[] {
  const map = new Map<string, ActiveCampaignGroup>();
  for (const row of alerts) {
    const id = row.alertCampaignId?.trim();
    if (!id) {
      continue;
    }
    let group = map.get(id);
    if (!group) {
      group = {
        campaignId: id,
        title: row.campaignTitle?.trim() || id,
        rows: [],
      };
      map.set(id, group);
    }
    group.rows.push(row);
  }
  return [...map.values()].sort((a, b) => earliestInitiated(a) - earliestInitiated(b));
}

function earliestInitiated(group: ActiveCampaignGroup): number {
  return Math.min(...group.rows.map((r) => r.initiatedOn));
}

export function buildCampaignTimeline(
  alertConfig: AlertConfig,
  campaignStartMs: number,
  scheduledEndMs: number | undefined,
  nowMs: number = Date.now(),
): CampaignTimelineModel | null {
  const slots = buildAlertCycle(alertConfig, campaignStartMs);
  const timeZone = resolveAlertTimeZone(alertConfig);
  const slotEpochs = slots
    .map((slot) => slotToEpoch(slot, timeZone))
    .filter((ms) => Number.isFinite(ms));

  if (!slotEpochs.length) {
    return null;
  }

  const timelineStartMs = campaignStartMs;
  let timelineEndMs =
    scheduledEndMs != null && Number.isFinite(scheduledEndMs)
      ? scheduledEndMs
      : slotEpochs[slotEpochs.length - 1];
  if (timelineEndMs <= timelineStartMs) {
    timelineEndMs = Math.max(slotEpochs[slotEpochs.length - 1], timelineStartMs + 1);
  }
  const span = Math.max(timelineEndMs - timelineStartMs, 1);

  const nowPercent = clampPercent(((nowMs - timelineStartMs) / span) * 100);
  const showNowMarker = nowMs >= timelineStartMs && nowMs <= timelineEndMs;
  const nowLabelAlign = resolveNowLabelAlign(nowPercent);
  let nextIndex = slotEpochs.findIndex((e) => e > nowMs);
  if (nextIndex < 0) {
    nextIndex = slotEpochs.length;
  }

  const dotPercents = computeSendDotPercents(slotEpochs);
  const dots: CampaignTimelineDot[] = slotEpochs.map((epochMs, index) => {
    const done = epochMs <= nowMs;
    return {
      index,
      epochMs,
      percent: dotPercents[index],
      done,
      isNext: index === nextIndex,
    };
  });

  return {
    startMs: timelineStartMs,
    endMs: timelineEndMs,
    nowMs,
    nowPercent,
    showNowMarker,
    nowLabelAlign,
    dots,
    startLabel: formatAlertStartedTime(timelineStartMs),
    endLabel: formatAlertStartedTime(timelineEndMs),
    nowLabel: formatAlertStartedTime(nowMs),
  };
}

/** Keep “Now” label on-screen when the marker sits near the timeline ends. */
export function resolveNowLabelAlign(nowPercent: number): NowLabelAlign {
  if (nowPercent <= 20) {
    return 'left';
  }
  if (nowPercent >= 80) {
    return 'right';
  }
  return 'center';
}

export type CampaignProgressSummary = {
  attempt: string;
  schedule: string;
};

function representativeActiveRow(rows: OrgActiveAlert[]): OrgActiveAlert | undefined {
  if (!rows.length) {
    return undefined;
  }
  return rows.reduce((best, row) => (row.followUps >= best.followUps ? row : best));
}

export function buildCampaignProgressSummary(
  group: ActiveCampaignGroup,
  campaignStartMs: number,
  scheduledEndMs: number | undefined,
  retryAttemptsFromConfig?: number,
): CampaignProgressSummary {
  const row = representativeActiveRow(group.rows);
  const attemptLabel = row
    ? formatNextAlertAttemptLabel({
        followUps: row.followUps,
        retryAttempts: row.retryAttempts ?? retryAttemptsFromConfig,
      })
    : null;
  const startLabel = formatAlertStartedTime(campaignStartMs);
  const endMs = scheduledEndMs ?? row?.scheduledEndAt;
  const scheduleParts = [`Start ${startLabel}`];
  if (endMs != null && Number.isFinite(endMs)) {
    scheduleParts.push(`End ${formatAlertStartedTime(endMs)}`);
  }
  return {
    attempt: attemptLabel ?? '',
    schedule: scheduleParts.join(' · '),
  };
}

/** History view passes no live rows; only show attempt while the campaign still has in-progress cycles. */
export function campaignHasInProgressAlerts(statusCounts: StatusCountEntry[]): boolean {
  return (statusCounts.find((entry) => entry.key === 'inProgress')?.count ?? 0) > 0;
}

/** First and last scheduled sends sit at 0% and 100%; middle sends scale by time between them. */
export function computeSendDotPercents(slotEpochs: number[]): number[] {
  if (!slotEpochs.length) {
    return [];
  }
  if (slotEpochs.length === 1) {
    return [0];
  }
  const firstMs = slotEpochs[0];
  const lastMs = slotEpochs[slotEpochs.length - 1];
  const sendSpan = lastMs - firstMs;
  return slotEpochs.map((epochMs, index) => {
    if (index === 0) {
      return 0;
    }
    if (index === slotEpochs.length - 1) {
      return 100;
    }
    if (sendSpan <= 0) {
      return clampPercent((index / (slotEpochs.length - 1)) * 100);
    }
    return clampPercent(((epochMs - firstMs) / sendSpan) * 100);
  });
}

export function statusCountsWithPercent(entries: StatusCountEntry[]): StatusCountWithPercent[] {
  const total = entries.reduce((sum, entry) => sum + entry.count, 0);
  if (total <= 0) {
    return entries.map((entry) => ({ ...entry, percent: 0 }));
  }
  return entries.map((entry) => ({
    ...entry,
    percent: Math.round((entry.count / total) * 100),
  }));
}

export function countStatusesByCycleKey(items: OrgAlertHistoryItem[]): StatusCountEntry[] {
  const counts = new Map<CycleStatusKey, number>();
  for (const item of items) {
    const key = cycleStatusBadgeVariant(item.status);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return STATUS_BAR_ORDER.map((key) => ({
    key,
    label: CYCLE_STATUS_LABELS[key],
    count: counts.get(key) ?? 0,
  }));
}

export function maxScheduledEndForGroup(rows: OrgActiveAlert[]): number | undefined {
  const ends = rows.map((r) => r.scheduledEndAt).filter((v): v is number => v != null);
  return ends.length ? Math.max(...ends) : undefined;
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/** Hours outside the configured alert window (when sends do not run). */
export function formatNoAlertWindowNote(alertConfig: AlertConfig): string {
  const window = alertConfig.notifyWindow;
  const alertStartHm = window?.start?.trim() || DEFAULT_NOTIFY_WINDOW.start;
  const alertEndHm = window?.end?.trim() || DEFAULT_NOTIFY_WINDOW.end;
  const timeZone = window?.timeZone?.trim() || DEFAULT_NOTIFY_WINDOW.timeZone;
  return `The no alert window is ${formatHmForDisplay(alertEndHm)}–${formatHmForDisplay(alertStartHm)}, ${timeZone}.`;
}

function formatHmForDisplay(hm: string): string {
  const [hourPart, minutePart] = hm.split(':');
  const hour = Number(hourPart);
  const minute = Number(minutePart ?? 0);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return hm;
  }
  const date = new Date(2000, 0, 1, hour, minute);
  return date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' });
}

